import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export function CheckEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const loc = useLocation();
  const stateEmail = (loc.state as { email?: string } | null)?.email;

  const [email, setEmail] = useState(stateEmail ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: (input: { email: string; code: string }) =>
      api<{ user: { id: string; email: string; role: "user" | "admin"; points: number } }>(
        "/api/auth/verify-code",
        { method: "POST", body: input },
      ),
    onSuccess: async () => {
      await refresh();
      navigate("/dashboard", { replace: true });
    },
    onError: (err: ApiError) => setError(err.message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cleanEmail = email.trim().toLowerCase();
    const cleanCode = code.replace(/\s/g, "");
    if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
      setError(t("checkEmail.codeErrorEmail"));
      return;
    }
    if (!/^\d{6}$/.test(cleanCode)) {
      setError(t("checkEmail.codeErrorFormat"));
      return;
    }
    verify.mutate({ email: cleanEmail, code: cleanCode });
  }

  return (
    <div className="container auth">
      <div className="surface auth__card">
        <h1>{t("checkEmail.title")}</h1>
        <p>
          {stateEmail ? (
            <Trans i18nKey="checkEmail.bodyWithEmail" values={{ email: stateEmail }} />
          ) : (
            <Trans i18nKey="checkEmail.bodyNoEmail" />
          )}
        </p>

        <form onSubmit={onSubmit} className="auth__code-form" noValidate>
          <p className="checkemail__hint">{t("checkEmail.codeHint")}</p>

          {!stateEmail && (
            <div className="field">
              <label htmlFor="ce-email">{t("checkEmail.emailLabel")}</label>
              <input
                id="ce-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={verify.isPending}
              />
            </div>
          )}

          <div className="field">
            <label htmlFor="ce-code">{t("checkEmail.codeLabel")}</label>
            <input
              id="ce-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              disabled={verify.isPending}
              placeholder="000000"
              className="auth__code-input"
              aria-describedby={error ? "ce-error" : undefined}
            />
          </div>

          {error && (
            <p id="ce-error" className="field__error" role="alert">{error}</p>
          )}

          <button type="submit" className="btn btn-primary" disabled={verify.isPending}>
            {verify.isPending ? t("checkEmail.codeSubmitting") : t("checkEmail.codeSubmit")}
          </button>
        </form>

        <p className="checkemail__fallback">
          <Trans
            i18nKey="checkEmail.fallback"
            components={{ 1: <Link to="/login" /> }}
          />
        </p>
      </div>
    </div>
  );
}
